<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import lottie, { type AnimationItem } from 'lottie-web/build/player/lottie_light';
import { useIsActive } from '@/use/active';

const props = defineProps<{ animationData: object }>();

const container = ref<HTMLDivElement | null>(null);
let animation: AnimationItem | undefined;

// The empty states sit in tab panels kept mounted off screen and in pages kept
// alive: a looping animation nobody sees would still redraw on every frame.
const isIntersecting = ref(false);
const { isSupported } = useIntersectionObserver(container, ([entry]) => {
  isIntersecting.value = entry?.isIntersecting ?? false;
});
const isActive = useIsActive();
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isPlaying = computed(
  () => !reduceMotion && (isIntersecting.value || !isSupported.value) && isActive.value,
);

function syncPlayback() {
  if (isPlaying.value) animation?.play();
  else animation?.pause();
}

onMounted(() => {
  if (!container.value) return;
  animation = lottie.loadAnimation({
    container: container.value,
    renderer: 'svg',
    loop: true,
    autoplay: false,
    animationData: props.animationData,
    rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
  });
  syncPlayback();
});

watch(isPlaying, syncPlayback);

onBeforeUnmount(() => {
  animation?.destroy();
});
</script>

<template>
  <div ref="container" aria-hidden="true" />
</template>
